import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { type Money } from "@src/appV2/lib/Money/types";
import { useCreateShiftInvitePrice } from "@src/appV2/ShiftInvites/api/useCreateShiftInvitePrice";
import { useEffect, useState } from "react";

import { type Professional, type ShiftDetails } from "../types";

export interface ChargeConstraints {
  min: Money;
  max: Money;
}

interface UseInviteChargeRateOptions {
  shiftDetails: ShiftDetails;
  selectedProfessional: Professional | undefined;
}

interface UseInviteChargeRateReturn {
  baseChargeRate: Money | undefined;
  chargeConstraints: ChargeConstraints | undefined;
  customChargeRateBoost: number | undefined;
  isCustomRateBoostEnabled: boolean;
  isPricingLoading: boolean;
  setCustomChargeRateBoost: (boost: number | undefined) => void;
  resetChargeRate: () => void;
}

export function useInviteChargeRate({
  shiftDetails,
  selectedProfessional,
}: UseInviteChargeRateOptions): UseInviteChargeRateReturn {
  const [baseChargeRate, setBaseChargeRate] = useState<Money | undefined>();
  const [chargeConstraints, setChargeConstraints] = useState<ChargeConstraints | undefined>();
  const [customChargeRateBoost, setCustomChargeRateBoost] = useState<number | undefined>();

  const { mutateAsync: fetchPriceAsync, isLoading: isPricingLoading } = useCreateShiftInvitePrice();
  const isCustomRateBoostEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_INVITE_CUSTOM_RATE_BOOST, {
    defaultValue: false,
  });

  const resetChargeRate = () => {
    setBaseChargeRate(undefined);
    setChargeConstraints(undefined);
    setCustomChargeRateBoost(undefined);
  };

  useEffect(() => {
    if (!isCustomRateBoostEnabled || !selectedProfessional) {
      setBaseChargeRate(undefined);
      setChargeConstraints(undefined);
      return;
    }

    // Clear stale pricing so the charge-rate UI shows its loading state until
    // the new price arrives. Without this, switching qualifications (or any
    // other pricing input) leaves the old price on screen during the fetch.
    setBaseChargeRate(undefined);
    setChargeConstraints(undefined);

    void fetchPriceAsync({
      qualification: shiftDetails.qualification,
      workerId: selectedProfessional.id,
      startAt: shiftDetails.startTime.toISOString(),
      endAt: shiftDetails.endTime.toISOString(),
      workplaceId: shiftDetails.workplaceId,
      shiftType: shiftDetails.timeSlot,
    })
      .then((response) => {
        setBaseChargeRate(response.data.attributes.charge);
        const chargeRange = response.data.attributes.constraints?.charge;
        if (chargeRange) {
          setChargeConstraints({
            min: chargeRange.minimumAmount,
            max: chargeRange.maximumAmount,
          });
        }
      })
      .catch(() => {
        // Pricing fetch failure is non-fatal; boost UI will not be shown
        setBaseChargeRate(undefined);
        setChargeConstraints(undefined);
      });
  }, [
    isCustomRateBoostEnabled,
    selectedProfessional,
    shiftDetails.qualification,
    shiftDetails.workplaceId,
    shiftDetails.timeSlot,
    shiftDetails.startTime,
    shiftDetails.endTime,
    fetchPriceAsync,
  ]);

  return {
    baseChargeRate,
    chargeConstraints,
    customChargeRateBoost,
    isCustomRateBoostEnabled,
    isPricingLoading,
    setCustomChargeRateBoost,
    resetChargeRate,
  };
}
